import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useTranslation } from "../../i18n";

/** Run states in which the campaign is still sending. */
const ACTIVE_RUN_STATES = new Set(["running", "stopping"]);

/**
 * Table listing all campaigns.
 *
 * The table is a navigation list: a row opens the campaign, and the actions
 * for that campaign — Edit, Test, Delete — live in the opened card, next to
 * its Close button. Per-row actions meant the same campaign offered Edit in
 * two places and packed four buttons into a column narrow enough to truncate
 * their labels; the row already had to be clicked to see anything about it.
 *
 * The left column keeps the campaign's primary control, which is its state
 * rather than an action on it, and depends on its kind: a **one-shot**
 * campaign (a broadcast, a VIP grant, a spend threshold) is *launched*, so it
 * gets a Launch button there in place of the enable toggle — it has no
 * schedule to enable, and offering both a toggle and a Run button for the
 * same action was confusing. A **scheduled** campaign keeps the
 * enable/disable toggle, since that is what arms its cron entry.
 *
 * `busyId` disables the controls for the campaign with an in-flight request,
 * preventing double-click double-sends. A running campaign is marked in the
 * name cell; the control that halts it — Stop — is in the card's run
 * progress, which prefers its own polled state and borrows this row's
 * snapshot only until that poll first answers: it polls the campaign on its
 * own, every 1.5s while a run is sending and on a 15s heartbeat once the run
 * is over. So a run started elsewhere shows up in the card too, and a run
 * that ended since this table's last refetch does not linger there as
 * stoppable.
 */
export default function CampaignTable({
  campaigns,
  triggerTypes,
  selectedId,
  busyId = null,
  onSelect,
  onToggle,
  onRun,
}) {
  const { t } = useTranslation();

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const isOneShot = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.oneShot ?? false;
  };

  if (campaigns.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm py-8 text-center">
        {t("campaign.noCampaigns")}
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-c-gray-400/30">
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colStatus")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colName")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colType")}
            </th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const busy = busyId === c.id;
            const oneShot = isOneShot(c.triggerType);
            const runState = c.run?.runState;
            const running = Boolean(
              runState && ACTIVE_RUN_STATES.has(runState),
            );
            return (
              <tr
                key={c.id}
                className={`border-b border-c-gray-400/10 hover:bg-c-gray-500/50 focus:bg-c-gray-500/50 focus:outline-none ${
                  selectedId === c.id ? "bg-accent/10" : ""
                }`}
                tabIndex={0}
                onClick={() => onSelect(c.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(c.id);
                  }
                }}
              >
                <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                  {oneShot ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy || running}
                      onClick={() => onRun(c.id)}
                    >
                      {t("campaign.launch")}
                    </Button>
                  ) : (
                    <Switch
                      checked={c.enabled}
                      disabled={busy || running}
                      onCheckedChange={() => onToggle(c.id, !c.enabled)}
                    />
                  )}
                </td>
                <td className="py-2 px-2 truncate max-w-[200px]">
                  {c.name || <span className="text-c-gray-400">#{c.id}</span>}
                  {running && (
                    <span className="ml-2 text-accent text-xs">
                      {t(`campaign.runStateShort.${runState}`)}
                    </span>
                  )}
                </td>
                <td className="py-2 px-2">{getLabel(c.triggerType)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
